import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { IPKImportLog, IPKSettings } from '../pk';
import { PkImportLogDetailedDialogComponent } from './pk-import-log-detailed-dialog/pk-import-log-detailed-dialog.component';
import { FiDateTimeFormatOptions } from '@fi-shared/utils';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ObjectTypes } from '@fi-shared/types';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { PkService } from '../pk.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { isDefined } from '@uirouter/core';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { PkHelperService } from '@fi-app/external-platform-integration/pk/pk-helper.service';

@Component({
    selector: 'pk-import-log',
    templateUrl: './pk-import-log.component.html',
    styleUrls: ['./pk-import-log.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pk-import-log' },
    standalone: false,
})
export class PkImportLogComponent extends FiRemoteStateFullDataSource<IPKImportLog> implements OnInit {
    @Input() selectedImportLogId: number;
    @Input() pkSettings: IPKSettings;
    defaultSortColumnName = 'ChangedOn';
    defaultSortDirection = true;
    itemsListType = FiItemsListType.PkImportLog;
    objectType = ObjectTypes.PKImportLog;

    constructor(
        injector: Injector,
        private pkService: PkService,
        private pkHelperService: PkHelperService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (isDefined(this.selectedImportLogId)) {
            // we should load single import log because currently loaded list can not contain needed item
            this.pkService.getImportLog(this.selectedImportLogId).then((importLog) => {
                this.openDetailedLogDialog(importLog);
            });
        }
    }

    loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: false,
        };
        return this.pkService.getImportLogListData(parameter);
    }

    getDateText(log: IPKImportLog) {
        let text = this.fiFormatDateService.formatDate(log.CreatedOn, FiDateTimeFormatOptions, { showToday: false });
        if (log.ErrorMessage) {
            text += ' Could not complete import!';
        }
        return text;
    }

    hasErrors(log: IPKImportLog) {
        return this.pkHelperService.hasErrors(log);
    }

    getRowClassFn = (log: IPKImportLog) => {
        return this.hasErrors(log) ? 'has-errors' : undefined;
    };

    openDetailedLogDialog(log: IPKImportLog) {
        this.fiDialogsService.custom({
            hideFooter: true,
            headerText: `${this.appTextService.get('pk.pk_import_log')} ${this.fiFormatDateService.formatDate(
                log.CreatedOn,
                'date-time',
                { showToday: false },
            )}`,
            contentComponentRef: PkImportLogDetailedDialogComponent,
            contentComponentData: { log, usersImportType: this.pkSettings.UsersImportType },
            config: {
                width: '700px',
            },
        });
    }
}
